import { createTheme, type MantineColorsTuple } from "@mantine/core";

/**
 * Generate a 10-shade color tuple from a single hex color.
 * Lightens for shades 0-4, uses original at 6, darkens for 7-9.
 */
export function generateShades(hex: string): MantineColorsTuple {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  const mix = (c: number, t: number, f: number) =>
    Math.round(c + (t - c) * f);

  const lighten = (factor: number) =>
    `#${[r, g, b].map((c) => mix(c, 255, factor).toString(16).padStart(2, "0")).join("")}`;

  const darken = (factor: number) =>
    `#${[r, g, b].map((c) => mix(c, 0, factor).toString(16).padStart(2, "0")).join("")}`;

  return [
    lighten(0.9),  // 0 - lightest
    lighten(0.75),
    lighten(0.6),
    lighten(0.45),
    lighten(0.3),
    lighten(0.15), // 5
    hex,           // 6 - original
    darken(0.15),
    darken(0.3),
    darken(0.45),  // 9 - darkest
  ];
}

// Nodus default blue
const NODUS_PRIMARY = "#3b82f6";
const NODUS_SECONDARY = "#10b981";

export const defaultNodusColors = {
  brand: generateShades(NODUS_PRIMARY),
  accent: generateShades(NODUS_SECONDARY),
};

/**
 * Default Nodus theme. Used when no site branding is provided.
 */
export const NodusTheme = createTheme({
  primaryColor: "brand",
  primaryShade: { light: 6, dark: 7 },
  colors: defaultNodusColors,
  defaultRadius: "md",
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  headings: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
  },
  cursorType: "pointer",
  respectReducedMotion: true,
  components: {
    Button: {
      defaultProps: { variant: "filled" },
    },
    TextInput: {
      defaultProps: { size: "sm" },
    },
    Select: {
      defaultProps: { size: "sm" },
    },
    Modal: {
      defaultProps: { centered: true },
    },
  },
});

export interface NodusBranding {
  primaryColor?: string;
  secondaryColor?: string;
  colorScheme?: "light" | "dark" | "auto";
}

/**
 * Create a Mantine theme with site-specific branding merged in.
 */
export function createBrandedTheme(branding?: NodusBranding | null) {
  if (!branding?.primaryColor) return NodusTheme;

  return createTheme({
    ...NodusTheme,
    colors: {
      ...defaultNodusColors,
      brand: generateShades(branding.primaryColor),
      ...(branding.secondaryColor
        ? { accent: generateShades(branding.secondaryColor) }
        : {}),
    },
  });
}
